<template>
  <div class="w-full order-1 md:order-2">
    <div class="bg-white p-6 rounded-lg shadow mb-6">
      <h3 class="text-sm text-center font-semibold mb-2">{{ $t('reservationSummary.title') }}</h3>
      <h4 class="text-xs text-center font-semibold text-gray-500 mb-4">{{ $t('reservationSummary.orderId') }} : {{
        bookingSummary?.Code }}</h4>

      <!-- Check-in dan Check-out -->
      <div class="text-sm text-gray-600 border rounded-lg p-4 mb-4">

        <div v-if="!showPrice" class="mb-2 border border-gray-300 rounded-lg shadow-sm p-4">
          <h2 class="text-base font-semibold text-gray-700">{{ bookingSummary?.PropertyName ?? '' }}</h2>
           <div v-for="detail in bookingDetail" :key="detail?.Id" class="">
            <!-- Header (Collapsible Trigger) -->
            <div class="flex justify-between items-center cursor-pointer rounded-lg">
              <!-- Nama Kamar -->
              <h2 class="text-base font-semibold text-gray-700">• {{ detail.RoomTypeName ? `${detail.RoomTypeName} - ` : `` }} {{ detail.Room?.Name }}</h2>

            </div>
          </div>
        </div>

        <div class="flex justify-between items-start p-4 border border-gray-300 rounded-lg">
          <!-- Check-in Section -->
          <div class="w-1/2">
            <span class="block font-semibold text-gray-500">{{ $t('reservationSummary.checkIn') }}</span>
            <div class="flex flex-col gap-1">
              <span class="text-gray-800 font-semibold">{{ $formatters.formatShortDate(bookingSummary?.CheckInDate)
                }}</span>
              <span class="text-blue-500 text-xs pl-1">{{ $t('reservationSummary.from') }} {{
                $formatters.formatTime(bookingSummary?.CheckInTime) }}</span>
            </div>
          </div>

          <!-- Border separator between Check-in and Check-out -->
          <div class="w-px bg-gray-300 h-full mx-2"></div>

          <!-- Check-out Section -->
          <div class="w-1/2">
            <span class="block font-semibold text-gray-500 pl-4">{{ $t('reservationSummary.checkOut') }}</span>
            <div class="border-l pl-4 flex flex-col gap-1">
              <span class="text-gray-800 font-semibold">{{ $formatters.formatShortDate(bookingSummary?.CheckOutDate)
                }}</span>
              <span class="text-blue-500 text-xs pl-1">{{ $t('reservationSummary.to') }} {{
                $formatters.formatTime(bookingSummary?.CheckOutTime) }}</span>
            </div>
          </div>
        </div>
        <div class="flex flex-col gap-2 mt-4 text-gray-600">
          <span>{{ $t('reservationSummary.totalLengthOfStay') }}</span>
          <div class="flex flex-row items-center gap-2">
            <span class="font-semibold">{{ bookingSummary?.TotalNights }} {{ bookingSummary?.TotalNights > 1 ?
              $t('reservationSummary.nights') : $t('reservationSummary.night') }}</span>
            <AppIcon icon="mdi:calendar-month-outline" class="w-5 h-5" />
          </div>
        </div>
      </div>

      <!-- Price Summary -->
      <div v-if="showPrice">
        <h4 class="text-lg font-semibold mt-6 mb-4">{{ $t('reservationSummary.yourPriceSummary') }}</h4>
        <div class="space-y-4">
          <div v-for="(detail, index) in bookingDetail" :key="detail?.Id" class="border rounded-lg shadow-sm p-4">
            <!-- Header (Collapsible Trigger) -->
            <div @click="toggleCollapse(index)" class="flex justify-between items-center cursor-pointer rounded-lg">
              <!-- Nama Kamar -->
              <h2 class="text-base font-semibold text-gray-700">{{ detail.Room?.Name }}</h2>

              <!-- Total Harga -->
              <div class="text-sm font-semibold text-gray-500 flex items-center gap-2">
                <span class="text-gray-900">{{ $formatters.formatCurrency(detail.Total) }}</span>
                <AppIcon :icon="collapsedIndex === index ? 'mdi:chevron-up' : 'mdi:chevron-down'"
                  class="w-5 h-5 text-gray-500" />
              </div>
            </div>

            <!-- Collapsible Content -->
            <div v-if="collapsedIndex === index" class="mt-4 space-y-2 text-sm text-gray-600">
              <!-- Price Details -->
              <div class="flex justify-between">
                <span>{{ $t('reservationSummary.pricePerNight', {
                  price: $formatters.formatCurrency(detail.PricePerNight),
                  nights: detail.TotalNights
                }) }}</span>
                <span class="font-semibold">{{ $formatters.formatCurrency(detail.SubTotal) }}</span>
              </div>

              <!-- Deposit -->
              <div v-if="detail.BookDetailDeposit" class="flex justify-between">
                <span>{{ $t('reservationSummary.deposit') }}</span>
                <span class="font-semibold">{{ $formatters.formatCurrency(detail.BookDetailDeposit.Total) }}</span>
              </div>

              <!-- Service Fee -->
              <div v-for="charge in detail.BookDetailCharges" :key="charge?.Id" class="flex justify-between">
                <span>{{ charge.RoomCharge.Charge.Name }}</span>
                <span class="font-semibold">{{ $formatters.formatCurrency(charge.Total) }}</span>
              </div>

              <!-- Tambahkan Rounding Jika Lebih dari 0.00 -->
              <div v-if="parseFloat(rounding) > 0.00"
                class="flex justify-between items-center text-sm text-gray-600 mt-2">
                <span>{{ $t('reservationSummary.rounding') }}</span>
                <span class="font-semibold">{{ $formatters.formatCurrency(parseFloat(rounding)) }}</span>
              </div>

              <!-- BookDetailAddOns -->
              <div v-if="detail.BookDetailAddOns && detail.BookDetailAddOns.length > 0" class="mt-4">
                <h5 class="text-sm font-semibold text-gray-500 mb-2">{{ $t('reservationSummary.addOns') }}</h5>
                <div v-for="addon in detail.BookDetailAddOns" :key="addon?.Id" class="flex justify-between">
                  <span>{{ addon.Name || addon.RoomAddOn?.AddOn?.Name }} x{{ addon.Quantity }}</span>
                  <span class="font-semibold">{{ $formatters.formatCurrency(addon.Total) }}</span>
                </div>
              </div>

              <!-- Separator -->
              <hr class="my-2" />

              <!-- Subtotal -->
              <div class="flex justify-between text-gray-500">
                <span>{{ $t('reservationSummary.subtotal') }}</span>
                <span class="font-semibold">{{ $formatters.formatCurrency(detail.Total) }}</span>
              </div>
            </div>
          </div>
        </div>
      

        <!-- Tambahkan Total-->
        <div class="mt-6">
          <div class="flex justify-between items-center text-lg font-bold text-blue-500">
            <span>{{ $t('reservationSummary.total') }}</span>
            <span>{{ $formatters.formatCurrency(bookingSummary?.Total) }}</span>
          </div>
        </div>
      </div>

    </div>

    <!-- <div class="flex flex-row justify-center w-full">
      <p class="text-xs text-gray-500">
        Dapatkan <span class="text-blue-500 font-semibold">+{{ $formatters.formatCurrency(bookingDetail.points) }}
          poin</span> setelah transaksi
      </p>
    </div> -->
  </div>
</template>

<script>
export default {
  name: 'PayVASummary',
  data() {
    return {
      collapsedIndex: null, // Index item yang sedang di-expand
    }
  },
  props: {
    bookingDetail: Array,
    bookingSummary: Object,
    selectedAddons: Array,
    selectedAddonsDetails: Array,
    voucherApplied: Boolean,
    discountAmount: Number,
    usePoints: Boolean,
    pointsToUse: Number,
    rounding: String,
    showPrice: {
      type: Boolean,
      default: true,
    },
  },
  methods: {
    toggleCollapse(index) {
      this.collapsedIndex = this.collapsedIndex === index ? null : index;
    },
    getChargesTotal(charges) {
      return charges.reduce((total, charge) => total + charge.Total, 0);
    },
  },
  mounted() {
    console.log('bookioniansc',this.bookingDetail)
  }
};
</script>

<style scoped>
/* Add any additional styling here if needed */
.collapsible-header {
  padding: 12px;
  background-color: #f9fafb;
  border-radius: 6px;
  transition: background-color 0.2s ease-in-out;
}

.collapsible-header:hover {
  background-color: #f3f4f6;
}

.collapsible-content {
  padding: 12px;
  border-top: 1px solid #e5e7eb;
}
</style>